import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useLogin, useCompanySelect, useLogout } from './index';
import { useStoreActions } from 'stores/global';
import { useEvent } from 'lib/segment';
import type { Login, LoginContext } from '../types';

interface UseLoginWithNavigationOptions {
  onSuccessNavigateTo?: string;
  onNoTokenNavigateTo?: string;
  onCompanySelectSuccessNavigateTo?: string;
  onLogoutSuccessNavigateTo?: string;
  onCompanySelectSuccess?: () => void;
}

export const useLoginWithNavigation = (options: UseLoginWithNavigationOptions = {}) => {
  const [searchParams] = useSearchParams();
  const event = useEvent();
  const returnUrl = searchParams.get('returnUrl') || '/';
  const companyReturnUrl =
    returnUrl === '/'
      ? '/switchcompany'
      : `/switchcompany?returnUrl=${encodeURIComponent(returnUrl)}`;
  const {
    onSuccessNavigateTo = returnUrl,
    onNoTokenNavigateTo = companyReturnUrl,
    onCompanySelectSuccessNavigateTo = returnUrl,
    onLogoutSuccessNavigateTo = '/',
    onCompanySelectSuccess,
  } = options;

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { resetPersistedState } = useStoreActions(state => state.urlState);

  const login = useLogin({
    onSuccess: (loginData: Login) => {
      queryClient.clear();
      if (loginData?.token) {
        navigate(onSuccessNavigateTo, { replace: true });
      } else {
        navigate(onNoTokenNavigateTo, { replace: true });
      }
    },
  });

  const companySelect = useCompanySelect({
    onSuccess: () => {
      queryClient.clear();
      navigate(onCompanySelectSuccessNavigateTo, { replace: true });
      resetPersistedState();
      onCompanySelectSuccess?.();
    },
  });

  const logout = useLogout({
    onSuccess: () => navigate(onLogoutSuccessNavigateTo, { replace: true }),
  });

  const submit = (user: string, password: string, RememberMe: boolean) => {
    login.login({ Username: user, Password: password, RememberMe });
  };

  const selectCompany = (loginContext: LoginContext, userData?: Login, length?: number) => {
    if (loginContext?.organizationType === 2) {
      event('Supplier Portal Login Context Selected', {
        objectType: 'Login Context',
        action: 'Selection',
        targetType: 'Organization Context',
        targetId: loginContext.organizationId,
        targetName: loginContext.organizationName,
        source: 'Login Selection',
        page: 'Multi-Select Login',
        businessContext: {
          userId: loginContext.id,
          loginContextType: 'supplier_portal',
          multipleContextsAvailable: length ? length > 1 : false,
        },
      });
    }
    companySelect.mutate({
      loginContext,
      Username: userData?.Username,
      Password: userData?.Password,
      user: userData?.user,
      RememberMe: userData?.RememberMe || false,
    });
  };

  const handleLogout = () => {
    logout();
  };

  return {
    login,
    companySelect,
    logout,
    submit,
    selectCompany,
    handleLogout,
  };
};
